/* ==========================================================================
   BRASSLUXE MOROCCO — collection filtering, sort & infinite scroll (ES module)
   Enhances sections/main-collection.liquid via the Section Rendering API:
   filter/sort changes replace the grid + filters + count, infinite scroll
   appends the next page's items. No-JS baseline stays fully functional.
   Runs through BrassLuxe.mount so it is Theme Editor safe.
   ========================================================================== */

const SELECTORS = {
  form: '[data-collection-form]',
  grid: '[data-collection-grid]',
  panel: '[data-collection-panel]',
  count: '[data-collection-count]',
  chips: '[data-active-filters]',
  pagination: '[data-collection-pagination]',
  sentinel: '[data-collection-sentinel]',
  spinner: '[data-collection-spinner]',
  endNote: '[data-collection-end]',
  toggle: '[data-filter-toggle]',
  close: '[data-filter-close]'
};

const DEBOUNCE_MS = 300;

window.BrassLuxe.mount('.main-collection', (root) => {
  const BLX = window.BrassLuxe;
  const sectionId = root.dataset.sectionId;
  const collectionUrl = root.dataset.collectionUrl;

  let controller = null;
  let debounceTimer = null;
  let observer = null;
  let loadingMore = false;

  root.classList.add('has-infinite-scroll');

  /* ---------- fetch helpers ---------- */

  function sectionUrl(url) {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    return u.toString();
  }

  async function fetchSection(url) {
    if (controller) controller.abort();
    controller = new AbortController();
    const response = await fetch(sectionUrl(url), {
      signal: controller.signal,
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Section fetch failed: ' + response.status);
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function swapInner(selector, doc) {
    const current = root.querySelector(selector);
    const incoming = doc.querySelector(selector);
    if (current && incoming) current.innerHTML = incoming.innerHTML;
  }

  function swapSentinel(doc) {
    const current = root.querySelector(SELECTORS.sentinel);
    const incoming = doc.querySelector(SELECTORS.sentinel);
    if (!current || !incoming) return;
    const clone = incoming.cloneNode(true);
    if (clone.dataset.nextUrl) {
      const u = new URL(clone.dataset.nextUrl, window.location.origin);
      u.searchParams.delete('section_id');
      clone.dataset.nextUrl = u.toString();
    }
    current.replaceWith(clone);
  }

  /* ---------- reveal: stagger fresh grid items, then wire the observer ---------- */

  function staggerReveal() {
    const grid = root.querySelector(SELECTORS.grid);
    if (!grid) return;
    const items = grid.querySelectorAll('[data-reveal]:not(.is-revealed)');
    items.forEach((el, i) => {
      el.style.setProperty('--reveal-delay', (i * 0.07).toFixed(2) + 's');
    });
    BLX.reveal(grid);
  }

  /* ---------- replace flow: filter / sort / chip / pagination / popstate ---------- */

  async function applyResults(url, push) {
    root.classList.add('is-loading');
    closePanel();
    try {
      const doc = await fetchSection(url);
      swapInner(SELECTORS.grid, doc);
      swapInner(SELECTORS.panel, doc);
      swapInner(SELECTORS.count, doc);
      swapInner(SELECTORS.chips, doc);
      swapInner(SELECTORS.pagination, doc);
      swapSentinel(doc);
      const endNote = root.querySelector(SELECTORS.endNote);
      if (endNote) endNote.hidden = true;
      if (push) window.history.pushState({ blxCollection: true }, '', url);
      staggerReveal();
      observeSentinel();
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = url;
    } finally {
      root.classList.remove('is-loading');
    }
  }

  function formParams() {
    const form = root.querySelector(SELECTORS.form);
    const params = new URLSearchParams();
    if (!form) return params;
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') params.append(key, value);
    });
    return params;
  }

  function applyFromForm() {
    const query = formParams().toString();
    applyResults(query ? collectionUrl + '?' + query : collectionUrl, true);
  }

  function scheduleApply() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(applyFromForm, DEBOUNCE_MS);
  }

  /* ---------- append flow: infinite scroll ---------- */

  function observeSentinel() {
    if (observer) observer.disconnect();
    if (!('IntersectionObserver' in window)) return;
    const sentinel = root.querySelector(SELECTORS.sentinel);
    if (!sentinel) return;
    const endNote = root.querySelector(SELECTORS.endNote);
    const grid = root.querySelector(SELECTORS.grid);

    if (sentinel.dataset.nextUrl) {
      if (endNote) endNote.hidden = true;
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) loadMore();
        });
      }, { rootMargin: '400px 0px' });
      observer.observe(sentinel);
    } else if (endNote && grid && grid.children.length > 0) {
      endNote.hidden = false;
    }
  }

  async function loadMore() {
    const sentinel = root.querySelector(SELECTORS.sentinel);
    const spinner = root.querySelector(SELECTORS.spinner);
    if (!sentinel || !sentinel.dataset.nextUrl || loadingMore) return;

    loadingMore = true;
    if (observer) observer.disconnect();
    if (spinner) spinner.hidden = false;

    let aborted = false;
    try {
      const doc = await fetchSection(sentinel.dataset.nextUrl);
      const grid = root.querySelector(SELECTORS.grid);
      const incomingGrid = doc.querySelector(SELECTORS.grid);
      if (grid && incomingGrid) {
        const fragment = document.createDocumentFragment();
        Array.from(incomingGrid.children).forEach((child) => {
          fragment.appendChild(child.cloneNode(true));
        });
        grid.appendChild(fragment);
      }
      swapSentinel(doc);
      staggerReveal();
    } catch (error) {
      if (error.name === 'AbortError') aborted = true;
    } finally {
      if (spinner) spinner.hidden = true;
      loadingMore = false;
    }
    if (!aborted) observeSentinel();
  }

  /* ---------- filter panel (mobile slide-over) ---------- */

  function togglePanel(toggle) {
    const panel = root.querySelector(SELECTORS.panel);
    if (!panel) return;
    const open = panel.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', String(open));
  }

  function closePanel() {
    const panel = root.querySelector(SELECTORS.panel);
    const toggle = root.querySelector(SELECTORS.toggle);
    if (panel) panel.classList.remove('is-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  /* ---------- delegated events (panel innerHTML is swapped on every fetch) ---------- */

  function onChange(event) {
    if (event.target.closest(SELECTORS.form)) scheduleApply();
  }

  function onInput(event) {
    const form = event.target.closest(SELECTORS.form);
    if (form && event.target.type === 'number') scheduleApply();
  }

  function onSubmit(event) {
    if (!event.target.closest(SELECTORS.form)) return;
    event.preventDefault();
    clearTimeout(debounceTimer);
    applyFromForm();
  }

  function onClick(event) {
    const toggle = event.target.closest(SELECTORS.toggle);
    if (toggle) {
      togglePanel(toggle);
      return;
    }
    if (event.target.closest(SELECTORS.close)) {
      closePanel();
      return;
    }
    const link = event.target.closest('a');
    if (link && (link.closest(SELECTORS.chips) || link.closest(SELECTORS.pagination))) {
      event.preventDefault();
      applyResults(link.href, true);
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') closePanel();
  }

  function onPopstate() {
    applyResults(window.location.href, false);
  }

  /* ---------- init + cleanup ---------- */

  root.addEventListener('change', onChange);
  root.addEventListener('input', onInput);
  root.addEventListener('submit', onSubmit);
  root.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);
  window.addEventListener('popstate', onPopstate);

  staggerReveal();
  observeSentinel();

  return function cleanup() {
    clearTimeout(debounceTimer);
    if (controller) controller.abort();
    if (observer) observer.disconnect();
    root.removeEventListener('change', onChange);
    root.removeEventListener('input', onInput);
    root.removeEventListener('submit', onSubmit);
    root.removeEventListener('click', onClick);
    document.removeEventListener('keydown', onKeydown);
    window.removeEventListener('popstate', onPopstate);
  };
});
